import { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';

import { Recipe, MealPlanItem } from '@/types/api';
import { WeeklyCalendar } from '@/components/meal-planner/WeeklyCalendar';
import { RecipeSidebar } from '@/components/meal-planner/RecipeSidebar';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent } from '@/components/ui/card';
import { 
  ChevronLeft, 
  ChevronRight, 
  Calendar,
  ShoppingCart,
  BarChart3,
  Plus,
  Check,
  X
} from 'lucide-react';

import {
  useUpdateMealPlanItem,
  useRemoveMealPlanItem,
  useMealPlanByWeek,
  useEnsureMealPlan,
  useCreateMealPlan,
  useHybridFavorites
} from '@/hooks';

import { 
  getWeekStart,
  getPreviousWeek,
  getNextWeek,
  formatWeekRange,
  getRelativeWeekDescription
} from '@/utils/dateUtils';

export function 
MealPlannerPage() {
  const navigate = useNavigate();
  const [currentWeekStart, setCurrentWeekStart] = useState(getWeekStart());

  // Get meal plan for current week
  const { 
    data: currentMealPlan, 
    isLoading: mealPlanLoading
  } = useMealPlanByWeek(currentWeekStart.toISOString().split('T')[0]);

  // Get next week's meal plan to check if it exists
  const nextWeekStart = getNextWeek(currentWeekStart);
  const { 
    data: nextWeekMealPlan, 
    isLoading: nextWeekLoading
  } = useMealPlanByWeek(nextWeekStart.toISOString().split('T')[0]);

  // Mutations
  const updateMealPlanItem = useUpdateMealPlanItem();
  const removeMealPlanItem = useRemoveMealPlanItem();
  const ensureMealPlan = useEnsureMealPlan();
  const createMealPlan = useCreateMealPlan();
  const favoritesHook = useHybridFavorites();

  // Check if we're viewing current week
  const isCurrentWeek = currentWeekStart.toDateString() === getWeekStart().toDateString();

  // Logic for next week suggestion
  const today = new Date();
  const currentWeekday = today.getDay(); // 0=Sunday, 1=Monday, ..., 6=Saturday
  const shouldSuggestNextWeek = useMemo(() => {
    // Suggest on Thursday (4), Friday (5), Saturday (6), Sunday (0)
    const suggestDays = [0, 4, 5, 6];
    return isCurrentWeek && 
           suggestDays.includes(currentWeekday) && 
           !nextWeekMealPlan && 
           !nextWeekLoading;
  }, [isCurrentWeek, currentWeekday, nextWeekMealPlan, nextWeekLoading]);

  // Navigation
  const goToPreviousWeek = () => {
    setCurrentWeekStart(getPreviousWeek(currentWeekStart));
  };

  const goToNextWeek = () => {
    setCurrentWeekStart(getNextWeek(currentWeekStart));
  };

  const goToCurrentWeek = () => {
    setCurrentWeekStart(getWeekStart());
  };

  const handleCreateNextWeek = async () => {
    try {
      // Create empty meal plan for next week
      await createMealPlan.mutateAsync({
        weekStart: nextWeekStart.toISOString()
      });
      // Navigate to next week after creating
      setCurrentWeekStart(nextWeekStart);
    } catch (error) {
      console.error('Failed to create next week plan:', error);
    }
  };


  // Recipe Actions
  const handleViewRecipe = (recipe: Recipe) => {
    navigate(`/recipes/${recipe.id}`);
  };

  // Calendar slot selection (for adding recipes from sidebar)
  const [selectedRecipe, setSelectedRecipe] = useState<Recipe | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  
  const handleSlotSelect = async (day: Date, service: 'comida' | 'cena') => {
    if (!selectedRecipe) {
      // TODO: Show message "Selecciona una receta del lateral primero"
      return;
    }

    try {
      await ensureMealPlan.mutateAsync({
        recipeId: selectedRecipe.id,
        dateISO: day.toISOString(),
        service,
      });
      
      // Show success message
      const dayName = day.toLocaleDateString('es-ES', { weekday: 'long' });
      const serviceName = service === 'comida' ? 'comida' : 'cena';
      setSuccessMessage(`${selectedRecipe.title} añadida a ${serviceName} del ${dayName}`);
      
      setSelectedRecipe(null); // Clear selection after adding
      
      // Clear success message after 3 seconds
      setTimeout(() => setSuccessMessage(null), 3000);
    } catch (error) {
      console.error('Failed to add recipe to meal plan:', error);
      // Error state will be shown in sidebar
    }
  };

  const handleEditServings = (item: MealPlanItem) => {
    // TODO: Implement servings modal
    const newServings = prompt(`Ajustar porciones para ${item.recipe.title}:`, item.servings.toString());
    
    if (newServings && !isNaN(parseInt(newServings))) {
      updateMealPlanItem.mutate({
        itemId: item.id,
        data: { servings: parseInt(newServings) }
      });
    }
  };

  const handleRemoveItem = (item: MealPlanItem) => {
    // La confirmación ahora se maneja en el componente MealSlot
    removeMealPlanItem.mutate(item.id);
  };

  const handleReplaceRecipe = (item: MealPlanItem) => {
    // TODO: Implement recipe replacement modal
    console.log('Replace recipe:', item.recipe.title);
  };

  const handleToggleFavorite = async (recipe: Recipe) => {
    try {
      await favoritesHook.toggleFavorite(recipe.id);
    } catch (error) {
      console.error('Failed to toggle favorite:', error);
    }
  };

  return (
    <>
      {/* Header */}
      <div className="bg-white border-b border-gray-200">
        <div className="container mx-auto px-4 py-4">
          <div className="space-y-4">
            {/* Top Navigation */}
            <div className="flex items-center justify-between gap-3">
              <div className="min-w-0 flex-1">
                <h1 className="text-xl sm:text-2xl font-bold text-gray-900 flex items-center gap-2">
                  <Calendar className="h-5 w-5 sm:h-6 sm:w-6" />
                  <span className="truncate">Planificador Semanal</span>
                </h1>
                <p className="text-sm text-gray-600 mt-1 hidden sm:block">
                  Selecciona recetas del lateral y haz clic en los slots del calendario
                </p>
              </div>

              <Button
                variant="outline"
                size="sm"
                onClick={() => navigate('/recipes')}
                className="flex items-center gap-2 h-9 touch-manipulation 
                           active:scale-95 transition-transform duration-150"
              >
                <Plus className="h-4 w-4" />
                <span className="hidden sm:inline">Buscar recetas</span>
              </Button>
            </div>

            {/* Week Navigation - 2 líneas en móvil */}
            <div className="space-y-3">
              {/* Fecha actual centrada */}
              <div className="text-center">
                <p className="font-semibold text-lg">
                  {formatWeekRange(currentWeekStart)}
                </p>
                <div className="flex items-center justify-center gap-2 text-sm text-gray-500 mt-1">
                  <span>{getRelativeWeekDescription(currentWeekStart)}</span>
                  {currentMealPlan && (
                    <>
                      <span>•</span>
                      <Badge variant="secondary" className="text-xs">
                        {currentMealPlan.items.length} comida{currentMealPlan.items.length !== 1 ? 's' : ''} planificada{currentMealPlan.items.length !== 1 ? 's' : ''}
                      </Badge>
                    </>
                  )}
                </div>
              </div>
              
              {/* Botones de navegación */}
              <div className="flex items-center justify-center gap-4">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={goToPreviousWeek}
                  className="h-10 px-4 flex-1 max-w-[140px] touch-manipulation 
                             active:scale-95 transition-transform duration-150"
                >
                  <ChevronLeft className="h-4 w-4 mr-1" />
                  Anterior
                </Button>
                
                {!isCurrentWeek && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={goToCurrentWeek}
                    className="h-10 px-3 text-xs touch-manipulation 
                               active:scale-95 transition-transform duration-150"
                  >
                    Hoy
                  </Button>
                )}
                
                <Button
                  variant="outline"
                  size="sm"
                  onClick={goToNextWeek}
                  className="h-10 px-4 flex-1 max-w-[140px] touch-manipulation 
                             active:scale-95 transition-transform duration-150 relative"
                >
                  Siguiente
                  <ChevronRight className="h-4 w-4 ml-1" />
                  {/* Indicator cuando próxima semana no tiene plan */}
                  {isCurrentWeek && !nextWeekMealPlan && !nextWeekLoading && (
                    <div className="absolute -top-1 -right-1 w-3 h-3 bg-orange-500 rounded-full border-2 border-white"></div>
                  )}
                </Button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Next Week Suggestion Banner */}
      {shouldSuggestNextWeek && (
        <div className="bg-gradient-to-r from-primary/10 to-primary/5 border-b border-primary/20">
          <div className="container mx-auto px-4 py-3">
            <div className="flex flex-col sm:flex-row items-center justify-between gap-3">
              <div className="flex items-center gap-3 text-center sm:text-left">
                <div className="flex-shrink-0 w-10 h-10 bg-primary/20 rounded-full flex items-center justify-center">
                  <Calendar className="h-5 w-5 text-primary" />
                </div>
                <div>
                  <p className="text-sm font-medium text-gray-900">
                    ¡Es hora de planificar la próxima semana!
                  </p>
                  <p className="text-xs text-gray-600">
                    Crea tu plan para {formatWeekRange(nextWeekStart)} y mantén tu rutina
                  </p>
                </div>
              </div>
              
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setCurrentWeekStart(nextWeekStart)}
                  className="h-9 text-xs"
                >
                  Ver próxima semana
                </Button>
                <Button
                  size="sm"
                  onClick={handleCreateNextWeek}
                  disabled={createMealPlan.isPending}
                  className="h-9 bg-primary hover:bg-primary/90"
                >
                  {createMealPlan.isPending ? (
                    <div className="flex items-center gap-2">
                      <div className="w-3 h-3 border border-white/30 border-t-white rounded-full animate-spin" />
                      Creando...
                    </div>
                  ) : (
                    <>
                      <Plus className="h-4 w-4 mr-1" />
                      Crear plan
                    </>
                  )}
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Success Message Banner */}
      {successMessage && (
        <div className="bg-green-50 border-b border-green-200">
          <div className="container mx-auto px-4 py-3">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="flex-shrink-0 w-8 h-8 bg-green-100 rounded-full flex items-center justify-center">
                  <Check className="h-4 w-4 text-green-600" />
                </div>
                <p className="text-sm font-medium text-green-800">
                  {successMessage}
                </p>
              </div>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setSuccessMessage(null)}
                className="h-7 w-7 p-0 text-green-600 hover:text-green-700"
              >
                <X className="h-3 w-3" />
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Main Content */}
      <div className="container mx-auto px-4 py-6">
        <div className="flex flex-col lg:grid lg:grid-cols-4 gap-6">
          {/* Recipe Sidebar - Arriba en móvil, lateral en desktop */}
          <div className="lg:col-span-1 order-1 lg:order-2">
            <RecipeSidebar
              onViewRecipe={handleViewRecipe}
              onRecipeSelect={setSelectedRecipe}
              selectedRecipe={selectedRecipe}
              isAddingToMealPlan={ensureMealPlan.isPending}
              addToMealPlanError={ensureMealPlan.error?.message || null}
              className="lg:sticky lg:top-4"
            />
          </div>

          {/* Meal Planner Calendar - Abajo en móvil, principal en desktop */}
          <div className="lg:col-span-3 order-2 lg:order-1">
            <WeeklyCalendar
              weekStart={currentWeekStart}
              mealPlan={currentMealPlan || undefined}
              onEditServings={handleEditServings}
              onRemoveItem={handleRemoveItem}
              onReplaceRecipe={handleReplaceRecipe}
              onSlotSelect={handleSlotSelect}
              onViewRecipe={handleViewRecipe}
              onToggleFavorite={handleToggleFavorite}
              isFavorite={favoritesHook.isFavorite}
              disabled={mealPlanLoading}
            />
          </div>
        </div>

        {/* Quick Actions - Mobile-friendly */}
        {currentMealPlan && (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 mt-6">
            <Card 
              className="hover:shadow-md transition-all duration-200 cursor-pointer 
                         active:scale-95 touch-manipulation"
              onClick={() => navigate(`/shopping-list/${currentMealPlan.id}`)}
            >
              <CardContent className="p-4 text-center">
                <ShoppingCart className="h-6 w-6 mx-auto mb-2 text-primary" />
                <p className="text-sm font-medium">Lista de Compras</p>
                <p className="text-xs text-gray-500">Generar automáticamente</p>
              </CardContent>
            </Card>

            <Card className="hover:shadow-md transition-all duration-200 cursor-pointer 
                             active:scale-95 touch-manipulation opacity-50">
              <CardContent className="p-4 text-center">
                <BarChart3 className="h-6 w-6 mx-auto mb-2 text-gray-400" />
                <p className="text-sm font-medium text-gray-500">Estadísticas</p>
                <p className="text-xs text-gray-400">Próximamente</p>
              </CardContent>
            </Card>

            <Card className="hover:shadow-md transition-all duration-200 cursor-pointer 
                             active:scale-95 touch-manipulation opacity-50">
              <CardContent className="p-4 text-center">
                <Calendar className="h-6 w-6 mx-auto mb-2 text-gray-400" />
                <p className="text-sm font-medium text-gray-500">Duplicar Semana</p>
                <p className="text-xs text-gray-400">Próximamente</p>
              </CardContent>
            </Card>

            <Card className="hover:shadow-md transition-all duration-200 cursor-pointer 
                             active:scale-95 touch-manipulation opacity-50">
              <CardContent className="p-4 text-center">
                <Plus className="h-6 w-6 mx-auto mb-2 text-gray-400" />
                <p className="text-sm font-medium text-gray-500">Sugerir Semana</p>
                <p className="text-xs text-gray-400">Próximamente</p>
              </CardContent>
            </Card>
          </div>
        )}
      </div>
    </>
  );
}
